import { useState, useEffect, useRef } from 'react';
import { Upload, MapPin, Check, Loader2, FileText, AlertTriangle, Mail, Copy, Inbox, CalendarClock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../services/api';
import { CanvassMap } from '../components/CanvassMap';
import { SqueegeeSync } from '../components/SqueegeeSync';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Canvassing({ user, onMenuClick }) {
  const isCanvasser = user?.role === 'Canvasser';
  const [records, setRecords] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [address, setAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [batches, setBatches] = useState([]);
  const [myBatches, setMyBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [vatInfo, setVatInfo] = useState({ enabled: false, pct: 20 });
  const [stripVat, setStripVat] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [showSqueegee, setShowSqueegee] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const mapFetchedFor = useRef('');
  const fileRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${getToken()}` };

  const loadBatches = () => {
    fetch(`${API}/api/canvassing/batches`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => setBatches(d.batches || []))
      .catch(() => {});
  };

  const loadMyBatches = () => {
    fetch(`${API}/api/canvassing/my-batches`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => setMyBatches(d.batches || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (isCanvasser) {
      loadMyBatches();
      return;
    }
    fetch(`${API}/api/rounds`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => setRounds((Array.isArray(d) ? d : d.rounds || []).map(r => r.name).filter(Boolean)))
      .catch(() => {});
    fetch(`${API}/api/canvassing/address`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => setAddress(d.address || ''))
      .catch(() => {});
    fetch(`${API}/api/settings`, { headers: authHeaders })
      .then(r => r.json())
      .then(d => {
        const enabled = !!d.vat_enabled;
        setVatInfo({ enabled, pct: d.vat_percentage || 20 });
        setStripVat(enabled);
      })
      .catch(() => {});
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch map pins once per review set (address signature guard stops
  // refetches while the owner edits prices/rounds inline).
  useEffect(() => {
    if (!records || isCanvasser) { setMapData(null); mapFetchedFor.current = ''; return; }
    const sig = records.map(r => r.address).join('|');
    if (sig === mapFetchedFor.current) return;
    mapFetchedFor.current = sig;
    setMapLoading(true);
    fetch(`${API}/api/canvassing/map-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ points: records.map(r => ({ name: r.name, address: r.address, round: r.round || null })) }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setMapData(d))
      .catch(() => {})
      .finally(() => setMapLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, isCanvasser]);

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reviewBatch = (b) => {
    setActiveBatch(b.id);
    setResult(null);
    setRecords(b.records.map(r => ({ ...r, round: r.suggested_round || '', use_schedule: !!r.schedule_reason })));
  };

  const dismissBatch = async (id) => {
    await fetch(`${API}/api/canvassing/batches/${id}/dismiss`, { method: 'POST', headers: authHeaders });
    loadBatches();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isCanvasser) {
        const res = await fetch(`${API}/api/canvassing/submit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Submit failed');
        toast.success(`Sheet submitted — ${data.records} customers sent to the office for review`);
        loadMyBatches();
        return;
      }
      const res = await fetch(`${API}/api/canvassing/extract`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Extraction failed');
      if (!data.records?.length) {
        toast.error('No customer records found in that file');
      } else {
        setRecords(data.records.map(r => ({ ...r, round: r.suggested_round || '', use_schedule: !!r.schedule_reason })));
        toast.success(`Found ${data.records.length} customers — review before importing`);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const update = (i, field, value) => {
    setRecords(prev => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };

  const removeRow = (i) => setRecords(prev => prev.filter((_, idx) => idx !== i));

  const doImport = async () => {
    setImporting(true);
    try {
      const payload = records.map(r => ({
        name: r.name, address: r.address, phone: r.phone, email: r.email,
        price: r.price ? Number(r.price) : null,
        initial_price: r.initial_price ? Number(r.initial_price) : null,
        frequency_weeks: r.frequency_weeks ? Number(r.frequency_weeks) : null,
        scheduled_date: r.scheduled_date || null,
        recurring_start: (r.use_schedule && r.recurring_start) ? r.recurring_start : null,
        notes: r.notes, round: r.round || null,
      }));
      const res = await fetch(`${API}/api/canvassing/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ records: payload, prices_include_vat: vatInfo.enabled && stripVat }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setResult(data);
      setRecords(null);
      if (activeBatch) {
        await fetch(`${API}/api/canvassing/batches/${activeBatch}/mark-imported`, { method: 'POST', headers: authHeaders });
        setActiveBatch(null);
        loadBatches();
      }
      toast.success(`Imported ${data.created_customers} customers with ${data.created_jobs} jobs`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="canvassing-page">
      {isCanvasser ? (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800">Canvassing</h1>
            <p className="text-sm text-slate-500 mt-1">
              Upload your completed canvassing sheet (PDF, CSV or Excel). It goes straight to the office
              for review — you can see the status of each submission below.
            </p>
          </div>

          <div
            className="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center bg-white hover:border-emerald-400 transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            data-testid="canvassing-submit-area"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.csv,.xlsx,.xls,.txt"
              className="hidden"
              onChange={e => handleFile(e.target.files?.[0])}
              data-testid="canvassing-file-input"
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                <p className="text-slate-600 font-medium">Reading your sheet…</p>
                <p className="text-xs text-slate-400">This can take up to a minute for big files</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-10 h-10 text-slate-400" />
                <p className="text-slate-700 font-medium">Tap to submit a canvassing sheet</p>
                <p className="text-xs text-slate-400">PDF, CSV or Excel — the office reviews and approves everything</p>
              </div>
            )}
          </div>

          {myBatches.length > 0 && (
            <div className="mt-6" data-testid="canvassing-my-submissions">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                <Inbox className="w-4 h-4 text-emerald-600" /> My submissions
              </p>
              <div className="space-y-2">
                {myBatches.map(b => (
                  <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`canvassing-my-batch-${b.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{b.filename} · {b.record_count} customers</p>
                      <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      b.status === 'imported' ? 'bg-emerald-100 text-emerald-700'
                        : b.status === 'dismissed' ? 'bg-slate-100 text-slate-500'
                        : 'bg-amber-100 text-amber-700'
                    }`} data-testid={`canvassing-my-batch-status-${b.id}`}>
                      {b.status === 'imported' ? 'Approved & imported' : b.status === 'dismissed' ? 'Not imported' : 'Awaiting review'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
      <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Canvassing</h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload a canvasser's sheet (PDF, CSV or Excel). New customers land on the Sunday of their
            first-clean week so you can slot them into your rounds — move them and answer Yes to the
            recurring question to lock in their day.
          </p>
        </div>
        <button
          onClick={() => setShowSqueegee(true)}
          className="shrink-0 px-3.5 py-2.5 bg-slate-800 text-white rounded-xl font-semibold text-sm flex items-center gap-2 hover:bg-slate-700"
          data-testid="open-squeegee-sync-btn"
        >
          <RefreshCw className="w-4 h-4" /> Squeegee Sync
        </button>
      </div>
      {showSqueegee && <SqueegeeSync onClose={() => setShowSqueegee(false)} onMenuClick={onMenuClick} />}

      {!records && (
        <>
          {address && (
            <div className="mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="canvassing-email-panel">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-600" /> Or have sheets emailed straight in
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Give this address to your canvassing company and ask them to CC it when they send you customer
                sheets — they'll appear below for you to review, and the canvasser gets an automatic confirmation.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-emerald-700 font-semibold" data-testid="canvassing-address">{address}</code>
                <button onClick={copyAddress} className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700" data-testid="canvassing-copy-address">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {batches.length > 0 && (
            <div className="mb-5" data-testid="canvassing-batches">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                <Inbox className="w-4 h-4 text-amber-500" /> Sheets waiting for your review
              </p>
              <div className="space-y-2">
                {batches.map(b => (
                  <div key={b.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`canvassing-batch-${b.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{b.filename} · {b.records?.length || 0} customers</p>
                      <p className="text-xs text-slate-500">From {b.submitted_by_name || b.from_email || 'unknown sender'} · {new Date(b.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => dismissBatch(b.id)} className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-500 hover:bg-white" data-testid={`canvassing-batch-dismiss-${b.id}`}>Dismiss</button>
                      <button onClick={() => reviewBatch(b)} className="px-4 py-1.5 text-xs rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700" data-testid={`canvassing-batch-review-${b.id}`}>Review</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!records && (
        <div
          className="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center bg-white hover:border-emerald-400 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
          data-testid="canvassing-upload-area"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls,.txt"
            className="hidden"
            onChange={e => handleFile(e.target.files?.[0])}
            data-testid="canvassing-file-input"
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
              <p className="text-slate-600 font-medium">Reading the sheet & matching rounds…</p>
              <p className="text-xs text-slate-400">AI extraction can take up to a minute for big files</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload className="w-10 h-10 text-slate-400" />
              <p className="text-slate-700 font-medium">Tap to upload a canvassing sheet</p>
              <p className="text-xs text-slate-400">PDF, CSV or Excel — nothing is saved until you review & confirm</p>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="canvassing-result">
          <p className="text-emerald-800 font-semibold flex items-center gap-2">
            <Check className="w-5 h-5" /> Imported {result.created_customers} customers, {result.created_jobs} jobs
            {result.rounds_created > 0 && `, ${result.rounds_created} new round${result.rounds_created !== 1 ? 's' : ''}`}
          </p>
          {result.skipped?.length > 0 && (
            <div className="mt-2 text-sm text-amber-700">
              <p className="font-medium flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Skipped:</p>
              {result.skipped.map((s, i) => <p key={i}>• {s}</p>)}
            </div>
          )}
        </div>
      )}

      {records && (
        <div data-testid="canvassing-review">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="w-4 h-4" /> {records.length} customers ready to review
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              {vatInfo.enabled && (
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" title="Sheet prices are what the customer pays — saving them ex-VAT means the VAT added at completion brings them back to the sheet price">
                  <input
                    type="checkbox"
                    checked={stripVat}
                    onChange={e => setStripVat(e.target.checked)}
                    className="w-3.5 h-3.5 accent-amber-600"
                    data-testid="canvassing-strip-vat"
                  />
                  Prices include VAT — save ex-VAT
                </label>
              )}
              <button
                onClick={() => setRecords(null)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="canvassing-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={doImport}
                disabled={importing}
                className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="canvassing-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Import {records.length} customers
              </button>
            </div>
          </div>

          {mapLoading && (
            <p className="text-xs text-slate-500 flex items-center gap-2 mb-3" data-testid="canvassing-map-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pinning addresses on the map…
            </p>
          )}
          {mapData && <CanvassMap data={mapData} />}

          <div className="space-y-3">
            {records.map((r, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`canvassing-row-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800">{r.name}</p>
                    <p className="text-sm text-slate-500">{r.address}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{r.phone} {r.email && `· ${r.email}`}</p>
                  </div>
                  <button onClick={() => removeRow(i)} className="text-xs text-red-400 hover:text-red-600" data-testid={`canvassing-remove-${i}`}>Remove</button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3">
                  <label className="text-xs text-slate-500">
                    Recurring £
                    <input type="number" step="0.01" value={r.price ?? ''} onChange={e => update(i, 'price', e.target.value)}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid={`canvassing-price-${i}`} />
                  </label>
                  <label className="text-xs text-slate-500">
                    First clean £
                    <input type="number" step="0.01" value={r.initial_price ?? ''} placeholder="same"
                      onChange={e => update(i, 'initial_price', e.target.value)}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid={`canvassing-initial-${i}`} />
                  </label>
                  <label className="text-xs text-slate-500">
                    Freq (wks)
                    <input type="number" value={r.frequency_weeks ?? ''} onChange={e => update(i, 'frequency_weeks', e.target.value)}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid={`canvassing-freq-${i}`} />
                  </label>
                  <label className="text-xs text-slate-500">
                    First job date
                    <input type="date" value={r.scheduled_date ?? ''} onChange={e => update(i, 'scheduled_date', e.target.value)}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid={`canvassing-date-${i}`} />
                  </label>
                  {r.recurring_start != null && (
                    <label className="text-xs text-slate-500" title={r.use_schedule ? 'Locked to the suggested date — untick the suggestion to change it' : ''}>
                      Recurring from
                      <input type="date" value={r.recurring_start ?? ''} onChange={e => update(i, 'recurring_start', e.target.value)}
                        disabled={!!r.use_schedule}
                        className={`mt-1 w-full border rounded-lg px-2 py-1.5 text-sm ${r.use_schedule ? 'border-sky-300 bg-sky-50 text-sky-800 cursor-not-allowed' : 'border-sky-200 bg-sky-50/50'}`}
                        data-testid={`canvassing-recurring-start-${i}`} />
                    </label>
                  )}
                  <label className="text-xs text-slate-500">
                    Round
                    <select value={r.round || ''} onChange={e => update(i, 'round', e.target.value)}
                      className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white" data-testid={`canvassing-round-${i}`}>
                      <option value="">— pick a round —</option>
                      {!rounds.includes(r.round) && r.round && <option value={r.round}>{r.round} (new)</option>}
                      {rounds.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </label>
                </div>

                <p className="text-xs mt-2 flex items-center gap-1 text-emerald-700" data-testid={`canvassing-reason-${i}`}>
                  <MapPin className="w-3.5 h-3.5" /> {r.round_reason}
                </p>
                {r.schedule_reason && (
                  <label className="text-xs mt-1 flex items-center gap-2 text-sky-700 cursor-pointer select-none" data-testid={`canvassing-schedule-reason-${i}`}>
                    <input
                      type="checkbox"
                      checked={!!r.use_schedule}
                      onChange={e => update(i, 'use_schedule', e.target.checked)}
                      className="w-3.5 h-3.5 accent-sky-600"
                      data-testid={`canvassing-use-schedule-${i}`}
                    />
                    <CalendarClock className="w-3.5 h-3.5" /> {r.schedule_reason}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.use_schedule ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-400'}`}>
                      {r.use_schedule ? 'USING SUGGESTION' : 'IGNORED'}
                    </span>
                  </label>
                )}
                {r.notes && <p className="text-xs text-slate-500 mt-1 bg-slate-50 rounded-lg p-2">{r.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

